import type {
    ListSupportFacilitiesRequest,
    ListSupportProvidersRequest,
    SupportCreateFacilityPayload,
    SupportCreateProviderPayload,
    SupportFacility,
    SupportProvider,
    SupportTag,
    SupportUpdateFacilityPayload,
    SupportUpdateProviderPayload,
} from '../../api';
import { ProviderType } from '../../api';
import {
    ROSTER_API_MAX_LIMIT,
    rosterApiCanSort,
    rosterFacilitiesRequest,
    rosterFacilityCreatePayload,
    rosterFacilityFromApi,
    rosterFacilityText,
    rosterFacilityUpdatePayload,
    rosterMergeSearchPages,
    rosterProviderCreatePayload,
    rosterProviderFromApi,
    rosterProvidersRequest,
    rosterProviderText,
    rosterProviderUpdatePayload,
    rosterSearchFilters,
    rosterSearchValue,
} from './roster.support-mapping';
import type {
    RosterApplyResult,
    RosterCapabilities,
    RosterClaim,
    RosterDataSource,
    RosterFacility,
    RosterFacilityDraft,
    RosterListQuery,
    RosterPage,
    RosterProvider,
    RosterProviderDraft,
    RosterRecord,
    RosterRecordRef,
    RosterTab,
    RosterTypeCounts,
} from './roster.types';

import type { RosterApiPage } from './roster.api-page';

export type { RosterApiPage } from './roster.api-page';

/**
 * The support endpoints the Roster uses, as plain functions. The app wires them to the generated
 * client (`createRosterSupportClients`); tests pass fakes, so the data source is tested without a
 * network or a DOM.
 */
export interface RosterSupportClients {
    listProviders(request: ListSupportProvidersRequest): Promise<RosterApiPage<SupportProvider>>;
    getProvider(id: string): Promise<SupportProvider>;
    createProvider(payload: SupportCreateProviderPayload): Promise<SupportProvider>;
    updateProvider(id: string, payload: SupportUpdateProviderPayload): Promise<void>;
    disableProvider(id: string): Promise<void>;
    listFacilities(request: ListSupportFacilitiesRequest): Promise<RosterApiPage<SupportFacility>>;
    getFacility(id: string): Promise<SupportFacility>;
    createFacility(payload: SupportCreateFacilityPayload): Promise<SupportFacility>;
    updateFacility(id: string, payload: SupportUpdateFacilityPayload): Promise<void>;
    disableFacility(id: string): Promise<void>;
    /** Active USER tags whose name is exactly `name`. */
    findTags(name: string): Promise<SupportTag[]>;
    createTag(name: string): Promise<SupportTag>;
    recordTag(tagId: string, target: { providerId?: string; facilityId?: string }): Promise<void>;
    /** The claims that reference the record — the app's claims list filtered by provider or facility. */
    listClaims(ref: RosterRecordRef): Promise<RosterClaim[]>;
    /**
     * Re-apply the record to these claims, and optionally retransmit them. No such route exists on
     * either API; this calls the one proposed in `docs/roster-backend-asks.md`, so the failure says so.
     */
    reapplyClaims(ref: RosterRecordRef, claimIds: string[], options: { retransmit: boolean }): Promise<RosterApplyResult>;
}

/** Ids per `filterId` request — keeps the URL well under length limits. */
const IDS_PER_REQUEST = 50;

/**
 * The Roster on the support API (FE-109 step 9).
 *
 * The claims that reference a record come from the real claims list. There is no endpoint to
 * re-apply a record to existing claims or retransmit them; since 2026-09-29 (user decision, to see
 * which endpoints fail) that step calls the route proposed in `docs/roster-backend-asks.md` rather
 * than being hidden, and its failure names the route. There is no "updated" time on providers or
 * facilities (checked 2026-09-25), so that column stays out.
 *
 * Bulk writes are one request per record — the API has no bulk endpoints — so a failure part-way
 * says how far it got; the store reloads either way.
 */
export class SupportRosterDataSource implements RosterDataSource {
    public readonly kind = 'api' as const;

    public readonly capabilities: RosterCapabilities = {
        write: true,
        // Wired to the proposed route (2026-09-29, user decision): the save flow runs end to end and
        // the missing endpoint fails visibly instead of being hidden.
        applyToClaims: true,
        updatedTimestamp: false,
        tags: true,
        // No re-apply endpoint (docs/roster-backend-asks.md): the claims step lists the real claims,
        // and "Update N claims" waits disabled under a "Coming soon" hint.
        comingSoon: ['applyToClaims'],
    };

    constructor(protected readonly clients: RosterSupportClients) {}

    public canSort(tab: RosterTab, key: string): boolean {
        return rosterApiCanSort(tab, key);
    }

    public async listProviders(query: RosterListQuery): Promise<RosterPage<RosterProvider>> {
        const request = rosterProvidersRequest(query);
        const search = query.search?.trim();
        if (!search) {
            const page = await this.clients.listProviders(request);
            return { items: page.items.map(rosterProviderFromApi), total: page.total };
        }
        const pages = await Promise.all(
            rosterSearchFilters('providers', search).map(async filter => {
                const page = await this.clients.listProviders({
                    ...request,
                    offset: 0,
                    limit: ROSTER_API_MAX_LIMIT,
                    [filter]: [`LK:${rosterSearchValue(filter, search)}`],
                });
                return { items: page.items.map(rosterProviderFromApi), total: page.total };
            }),
        );
        return rosterMergeSearchPages(pages, query, rosterProviderText);
    }

    public async listFacilities(query: RosterListQuery): Promise<RosterPage<RosterFacility>> {
        const request = rosterFacilitiesRequest(query);
        const search = query.search?.trim();
        if (!search) {
            const page = await this.clients.listFacilities(request);
            return { items: page.items.map(rosterFacilityFromApi), total: page.total };
        }
        const pages = await Promise.all(
            rosterSearchFilters('facilities', search).map(async filter => {
                const page = await this.clients.listFacilities({
                    ...request,
                    offset: 0,
                    limit: ROSTER_API_MAX_LIMIT,
                    [filter]: [`LK:${rosterSearchValue(filter, search)}`],
                });
                return { items: page.items.map(rosterFacilityFromApi), total: page.total };
            }),
        );
        return rosterMergeSearchPages(pages, query, rosterFacilityText);
    }

    public async listByIds(tab: RosterTab, ids: string[]): Promise<RosterRecord[]> {
        const records: RosterRecord[] = [];
        for (let i = 0; i < ids.length; i += IDS_PER_REQUEST) {
            const filterId = ids.slice(i, i + IDS_PER_REQUEST).map(id => `EQ:${id}`);
            if (tab === 'providers') {
                const page = await this.clients.listProviders({
                    filterId,
                    filterActive: 'EQ:1',
                    limit: ROSTER_API_MAX_LIMIT,
                });
                records.push(...page.items.map(rosterProviderFromApi));
            } else {
                const page = await this.clients.listFacilities({
                    filterId,
                    filterActive: 'EQ:1',
                    limit: ROSTER_API_MAX_LIMIT,
                });
                records.push(...page.items.map(rosterFacilityFromApi));
            }
        }
        return records;
    }

    /** One request per type — the API has no grouped count — plus the unfiltered total. */
    public async countProvidersByType(): Promise<RosterTypeCounts> {
        const types = Object.values(ProviderType) as ProviderType[];
        const [all, ...perType] = await Promise.all([
            this.clients.listProviders({ filterActive: 'EQ:1', limit: 1 }),
            ...types.map(type =>
                this.clients.listProviders({ filterActive: 'EQ:1', filterProviderType: [`EQ:${type}`], limit: 1 }),
            ),
        ]);
        const byType: RosterTypeCounts['byType'] = {};
        types.forEach((type, index) => {
            byType[type] = perType[index].total;
        });
        return { total: all.total, byType };
    }

    public async saveProvider(draft: RosterProviderDraft): Promise<RosterProvider> {
        if (!draft.id) {
            return rosterProviderFromApi(await this.clients.createProvider(rosterProviderCreatePayload(draft)));
        }
        const current = await this.clients.getProvider(draft.id);
        await this.clients.updateProvider(draft.id, rosterProviderUpdatePayload(current, draft));
        return rosterProviderFromApi(await this.clients.getProvider(draft.id));
    }

    public async saveFacility(draft: RosterFacilityDraft): Promise<RosterFacility> {
        if (!draft.id) {
            return rosterFacilityFromApi(await this.clients.createFacility(rosterFacilityCreatePayload(draft)));
        }
        const current = await this.clients.getFacility(draft.id);
        await this.clients.updateFacility(draft.id, rosterFacilityUpdatePayload(current, draft));
        return rosterFacilityFromApi(await this.clients.getFacility(draft.id));
    }

    public async disable(tab: RosterTab, ids: string[]): Promise<void> {
        await this.eachOf(ids, 'Disabled', id =>
            tab === 'providers' ? this.clients.disableProvider(id) : this.clients.disableFacility(id),
        );
    }

    /**
     * Find the tag by its exact name — the server stores names lower-cased, and a substring search
     * could hide it behind longer names and create it twice (FE-89) — or create it; then record it
     * on each selected record that does not already carry it.
     */
    public async addTag(tab: RosterTab, ids: string[], tagName: string): Promise<void> {
        const name = tagName.trim().toLowerCase();
        if (!name) {
            throw new Error('Tag name is required');
        }
        const existing = (await this.clients.findTags(name)).find(tag => (tag.name ?? '').toLowerCase() === name);
        const tag = existing ?? (await this.clients.createTag(name));
        if (!tag.id) {
            throw new Error('The tag was not created');
        }
        const tagId = tag.id;
        const records = await this.listByIds(tab, ids);
        const untagged = records.filter(record => !record.tags.some(t => t.id === tagId));
        await this.eachOf(
            untagged.map(record => record.id),
            'Tagged',
            id => this.clients.recordTag(tagId, tab === 'providers' ? { providerId: id } : { facilityId: id }),
        );
    }

    public listClaimsFor(ref: RosterRecordRef): Promise<RosterClaim[]> {
        return this.clients.listClaims(ref);
    }

    public applyToClaims(
        ref: RosterRecordRef,
        claimIds: string[],
        options: { retransmit: boolean },
    ): Promise<RosterApplyResult> {
        return this.clients.reapplyClaims(ref, claimIds, options);
    }

    /** Runs a per-record write in order; a failure reports how many went through first. */
    private async eachOf(ids: string[], verb: string, write: (id: string) => Promise<void>): Promise<void> {
        for (let i = 0; i < ids.length; i += 1) {
            try {
                await write(ids[i]);
            } catch (error) {
                const reason = error instanceof Error && error.message ? error.message : 'request failed';
                throw new Error(`${verb} ${i} of ${ids.length} before a failure: ${reason}`);
            }
        }
    }
}
